import { Fragment, type JSXElementConstructor, type MouseEvent, type ReactElement } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Checkbox, Collapse, IconButton, Stack, SxProps, TableCell, TableRow } from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import type { ActionsMenuProps } from '@/components/table/actions/ActionsMenu/ActionsMenu'
import { Theme } from '@/styles/theme'

import { BASE_TABLE_TEST_IDS } from '../TableBase.constants'
import type { Column } from '../TableBase.types'
import { Cell } from './Cell'

type Props<Data extends Record<string, unknown>, SortAttribute extends string> = {
  row: Data & { __id: string }
  columns: Column<Data, SortAttribute>[]
  withCheckbox?: boolean
  totalColumns: number
  isSelected: boolean
  isRowCheckable: boolean
  isRowClickable: boolean
  isRowDetailsOpen?: boolean
  rowActions?: ReactElement<ActionsMenuProps<Data>, JSXElementConstructor<ActionsMenuProps<Data>>>
  rowSx?: SxProps<Theme>
  onRowClick?: (row: Data & { __id: string }) => void
  handleRowCheck: (id: string, checked: boolean) => void
  renderRowDetails?: (row: Data & { __id: string }) => ReactElement
  toggleRowDetails: (rowId: string) => void
  checkColumnVisibility: (column: string | Column<Data, SortAttribute>) => boolean
}

export const Row = <Data extends Record<string, unknown>, SortAttribute extends string>({
  row,
  columns,
  isSelected,
  rowActions,
  withCheckbox,
  totalColumns,
  isRowCheckable,
  isRowClickable,
  isRowDetailsOpen,
  rowSx,
  onRowClick,
  handleRowCheck,
  toggleRowDetails,
  renderRowDetails,
  checkColumnVisibility
}: Props<Data, SortAttribute>) => {
  const rowKey = row.__id
  const detailsId = `details-${row.__id}`
  const hasRowDetails = !!renderRowDetails
  const hasRowActions = !!rowActions

  const handleRowDetailsToggle = (e: MouseEvent) => {
    if (!hasRowDetails) return

    e.stopPropagation()
    toggleRowDetails?.(rowKey)
  }

  return (
    <Fragment key={rowKey}>
      <TableRow
        hover
        selected={isSelected}
        data-testid={BASE_TABLE_TEST_IDS.row}
        onClick={(e) => {
          if (!isRowClickable) return
          const interactiveTags = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL']
          const target = e.target as HTMLElement

          // Check for interactive tags
          if (interactiveTags.includes(target.tagName)) return

          // Check if inside actions cell
          if (target.closest('.row-actions-cell')) return

          // Check if inside any open MUI menu/popover
          if (
            target.closest('.MuiMenu-root') ||
            target.closest('.MuiPopover-root') ||
            target.closest('.MuiMenu-paper')
          )
            return

          onRowClick?.(row)
        }}
        aria-expanded={hasRowDetails ? isRowDetailsOpen : undefined}
        sx={{
          cursor: isRowClickable ? 'pointer' : 'default',
          '& > *': {
            borderBottom: hasRowDetails ? 'none !important' : undefined
          },
          '& > :first-of-type': {
            pl: withCheckbox ? 0.85 : 2.25
          },
          ...rowSx,
          '& > :nth-of-type(2)': {
            pl: withCheckbox ? 1.25 : 2
          },
          '& > :last-child': {
            pr: hasRowActions ? 1 : 2
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }
        }}
      >
        {(withCheckbox || hasRowDetails) && (
          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
            <Stack direction="row">
              {withCheckbox && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => handleRowCheck(rowKey, e.target.checked)}
                  disabled={!isRowCheckable}
                  color="secondary"
                />
              )}

              {hasRowDetails && (
                <ConsoleTooltip
                  title={isRowDetailsOpen ? 'Collapse Details' : 'Expand Details'}
                  enterDelay={300}
                  enterNextDelay={500}
                >
                  <IconButton
                    aria-label={isRowDetailsOpen ? 'Collapse row details' : 'Expand row details'}
                    aria-controls={detailsId}
                    size="small"
                    sx={{ p: 1.2 }}
                    onClick={handleRowDetailsToggle}
                  >
                    {isRowDetailsOpen ? (
                      <ExpandMoreIcon data-testid={BASE_TABLE_TEST_IDS.collapseIcon} />
                    ) : (
                      <KeyboardArrowRightIcon data-testid={BASE_TABLE_TEST_IDS.expandMoreIcon} />
                    )}
                  </IconButton>
                </ConsoleTooltip>
              )}
            </Stack>
          </TableCell>
        )}

        {columns.filter(checkColumnVisibility).map((column) => (
          <Cell key={`${row.__id}-${column.id}`} column={column} row={row} />
        ))}

        {rowActions && (
          <TableCell
            data-testid={BASE_TABLE_TEST_IDS.rowActions}
            align="right"
            padding="checkbox"
            className="row-actions-cell"
          >
            <rowActions.type
              key={rowActions.key}
              {...rowActions.props}
              row={row}
              disabled={!isRowCheckable}
            >
              {rowActions.props.children}
            </rowActions.type>
          </TableCell>
        )}
      </TableRow>

      {hasRowDetails && (
        <TableRow data-testid={BASE_TABLE_TEST_IDS.detailsRow}>
          <TableCell id={detailsId} sx={{ p: 0 }} colSpan={totalColumns}>
            <Collapse in={isRowDetailsOpen} timeout="auto" unmountOnExit>
              {renderRowDetails(row)}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}
